import { z } from 'zod';

/**
 * Runtime-validated environment. Parsed ONCE at boot; the app fails fast on
 * misconfiguration instead of crashing deep in a feature (ARCHITECTURE §9).
 *
 * SECURITY: every VITE_* var is inlined into the public bundle — NEVER put secrets here.
 */
const EnvSchema = z.object({
  /** Backend (Riloma-API) base URL. '' = same-origin (Vite proxy in dev / same host in prod). */
  VITE_API_BASE_URL: z.string().default(''),
  /** 'true' enables MSW mocking. */
  VITE_API_MOCKING: z.enum(['true', 'false']).default('false'),
  /** Sentry DSN; empty disables Sentry. */
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  /** Google Maps/Places browser key (HTTP-referrer restricted in Cloud Console — not a secret,
   *  same public-but-restricted model as the mobile app's key). Empty disables location search. */
  VITE_GOOGLE_MAPS_API_KEY: z.string().default(''),
  /** Google OAuth 2.0 Web Client ID for the token client (popup) flow. */
  VITE_GOOGLE_CLIENT_ID: z.string().default(''),
  /** Apple Services ID (e.g. com.riloma.split.web) — distinct from the iOS/Android bundle ID. */
  VITE_APPLE_CLIENT_ID: z.string().default(''),
  /** Return URL registered on the Apple Services ID. Required by AppleID.auth.init even in
   *  popup mode; must exactly match what's configured in Apple Developer Portal. */
  VITE_APPLE_REDIRECT_URI: z.string().default(''),
});

export type Env = z.infer<typeof EnvSchema>;

function parseEnv(): Env {
  const result = EnvSchema.safeParse(import.meta.env);
  if (!result.success) {
    // eslint-disable-next-line no-console -- boot-time fatal, before any logger exists
    console.error('[env] Invalid configuration:', z.treeifyError(result.error));
    throw new Error('Invalid environment configuration — see console.');
  }
  return result.data;
}

export const env = parseEnv();

/** Mock the network (MSW) when explicitly enabled. */
export const isMockingEnabled = env.VITE_API_MOCKING === 'true';
